import { createContext, useContext, useState } from 'react';

const AuthContext = createContext(null);

function decode(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

// Read token synchronously so the very first render of ProtectedRoute already
// knows whether the user is authenticated. Without this, a page-load on a
// protected route bounces through /login → /dashboard before useEffect hydrates
// state, breaking deep-linking.
function initialAuthState() {
  if (typeof window === 'undefined') return { user: null, roles: [] };
  const token = window.localStorage.getItem('token');
  if (!token) return { user: null, roles: [] };
  const payload = decode(token);
  if (!payload) {
    window.localStorage.removeItem('token');
    return { user: null, roles: [] };
  }
  return { user: payload, roles: payload.roles || [] };
}

export function AuthProvider({ children }) {
  const initial = initialAuthState();
  const [user, setUser] = useState(initial.user);
  const [roles, setRoles] = useState(initial.roles);

  const login = (token) => {
    localStorage.setItem('token', token);
    const payload = decode(token);
    setUser(payload);
    setRoles(payload?.roles || []);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setRoles([]);
  };

  const hasRole = (...required) => required.some((r) => roles.includes(r));
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, roles, isAuthenticated, hasRole, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
